import { forwardRef, PropsWithRef, useMemo } from 'react';
import { TextField as MUITextField, TextFieldProps } from '@mui/material';
import { EventType } from '@/lib/types';
import { red, slate, white } from '@/lib/colours';
import { Skeleton } from '../Skeleton';
import debounce from 'lodash/debounce';

const DEBOUNCE_DELAY = 500;

export type CustomTextFieldProps = PropsWithRef<TextFieldProps> & {
  loading?: boolean;
  trackChange?: (event: any) => void;
  trackFocus?: (event: any) => void;
  trackEventName?: string;
  eventType?: EventType;
};

export const TextField = forwardRef<HTMLDivElement, CustomTextFieldProps>(
  function TextField(
    {
      loading = false,
      trackChange,
      trackFocus,
      trackEventName,
      eventType = EventType.INPUT_CHANGE,
      ...props
    },
    ref,
  ) {
    // Debounce function to prevent excessive logging
    const debouncedTrackChange = useMemo(() => {
      return debounce((value: string) => {
        if (trackChange && trackEventName) {
          trackChange({
            name: trackEventName,
            properties: {
              eventType,
              fieldName: trackEventName,
              fieldValue: value,
            },
          });
        }
      }, DEBOUNCE_DELAY);
    }, [trackChange, trackEventName, eventType]);

    if (loading) {
      return <Skeleton variant="text" />;
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value ?? '';

      // Use debounced function for tracking
      debouncedTrackChange(value);

      if (props.onChange) {
        props.onChange(event);
      }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (trackFocus && trackEventName) {
        trackFocus({
          name: trackEventName,
          properties: {
            eventType: EventType.INPUT_FOCUS,
            fieldName: trackEventName,
          },
        });
      }
      if (props.onFocus) {
        props.onFocus(event);
      }
    };

    if (props.variant === 'filled') {
      return (
        <MUITextField
          {...props}
          ref={ref}
          required={props.disabled ? false : props.required}
          error={props.disabled ? false : props.error}
          onChange={handleChange}
          onFocus={handleFocus}
          InputLabelProps={{
            shrink: true,
            ...props.InputLabelProps,
            sx: {
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: '500',
              lineHeight: '20px',
              letterSpacing: '0.25px',
              paddingTop: '3px',
              paddingLeft: '1px',
              ...props.InputLabelProps?.sx,
            },
          }}
          InputProps={{
            disableUnderline: true,
            ...props.InputProps,
            sx: {
              backgroundColor: white[900],
              borderRadius: '8px',
              border: `1px solid ${slate[300]}`,
              ':focus-within': {
                borderColor: slate[300],
              },
              ':has(input[aria-invalid="true"])': {
                borderColor: red[600],
              },
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: '20px',
              letterSpacing: '0.25px',
              '&.MuiFilledInput-root': {
                backgroundColor: 'transparent',
              },
              '.Mui-disabled': {
                borderRadius: '8px',
                fontStyle: 'italic',
              },
              ...props.InputProps?.sx,
            },
          }}
          sx={{
            overflow: 'hidden',
            borderRadius: '8px',
            ...props.sx,
          }}
        />
      );
    }

    return (
      <MUITextField
        {...props}
        ref={ref}
        onChange={handleChange}
        onFocus={handleFocus}
      />
    );
  },
);
